// FolderTree — sidebar nested folder navigation for the Documents page.
// Self-contained: loads its own data, handles its own create/rename/delete,
// and notifies the parent via onSelect / onCountsChanged.
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Folder, FolderOpen, FolderPlus, ChevronRight, ChevronDown,
  Plus, Trash2, PencilLine, Inbox, Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function FolderTree({ clientId, token, selectedId, onSelect, refreshSignal = 0, onDocumentsChanged }) {
  const [folders, setFolders] = useState([]);
  const [unfiledCount, setUnfiledCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [creatingUnder, setCreatingUnder] = useState(null);  // parent folder id or '__root__'
  const [newName, setNewName] = useState('');
  const [renaming, setRenaming] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    if (!token || !clientId) return null;
    try {
      const r = await axios.get(`${BACKEND_URL}/api/clients/${clientId}/folders`, { headers });
      return r.data;
    } catch (err) {
      toast.error('Could not load folders');
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, clientId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await load();
      if (cancelled) return;
      if (data) {
        setFolders(data.folders || []);
        setUnfiledCount(data.unfiledCount || 0);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [load, refreshSignal]);

  const refresh = useCallback(async () => {
    const data = await load();
    if (data) {
      setFolders(data.folders || []);
      setUnfiledCount(data.unfiledCount || 0);
    }
  }, [load]);

  const createFolder = async (parentId) => {
    const name = newName.trim();
    if (!name) return;
    try {
      await axios.post(`${BACKEND_URL}/api/clients/${clientId}/folders`, { name, parentId }, { headers });
      toast.success('Folder created');
      setNewName('');
      setCreatingUnder(null);
      if (parentId) setExpanded((e) => ({ ...e, [parentId]: true }));
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create folder');
    }
  };

  const renameFolder = async (id) => {
    const name = renameValue.trim();
    if (!name) return;
    try {
      await axios.patch(`${BACKEND_URL}/api/clients/${clientId}/folders/${id}`, { name }, { headers });
      toast.success('Renamed');
      setRenaming(null);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rename failed');
    }
  };

  const deleteFolder = async (folder) => {
    const ok = window.confirm(
      `Delete "${folder.name}"?` + (folder.documentCount > 0
        ? `\n\nThis folder has ${folder.documentCount} document${folder.documentCount === 1 ? '' : 's'}. They will be moved to Unfiled.`
        : ''),
    );
    if (!ok) return;
    try {
      await axios.delete(
        `${BACKEND_URL}/api/clients/${clientId}/folders/${folder.id}?cascade=true`,
        { headers },
      );
      toast.success('Folder deleted');
      if (selectedId === folder.id) onSelect?.(null);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  // Drop a document onto a folder (or onto Unfiled root)
  const handleDrop = async (folderId, e) => {
    e.preventDefault();
    e.stopPropagation();
    const data = e.dataTransfer.getData('application/x-qindex-document');
    if (!data) return;
    let documentIds;
    try { documentIds = JSON.parse(data); } catch { return; }
    if (!Array.isArray(documentIds) || documentIds.length === 0) return;
    try {
      await axios.post(
        `${BACKEND_URL}/api/clients/${clientId}/documents/move`,
        { documentIds, folderId: folderId || null },
        { headers },
      );
      toast.success(documentIds.length === 1 ? 'Document moved' : `${documentIds.length} documents moved`);
      refresh();
      onDocumentsChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Move failed');
    }
  };

  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };

  const rootFolders = folders.filter((f) => !f.parentId);
  const childrenOf = (id) => folders.filter((f) => f.parentId === id);

  if (loading) {
    return (
      <div className="flex justify-center py-6" data-testid="folder-tree-loading">
        <Loader2 className="w-4 h-4 animate-spin text-[#8C8078]" />
      </div>
    );
  }

  return (
    <div className="space-y-1" data-testid="folder-tree">
      {/* All documents (virtual root) */}
      <FolderRow
        label="All documents"
        icon={Inbox}
        active={selectedId === '__all__'}
        onClick={() => onSelect?.('__all__')}
        onDragOver={onDragOver}
        onDrop={(e) => handleDrop(null, e)}
        testId="folder-row-all"
      />
      {/* Unfiled */}
      <FolderRow
        label="Unfiled"
        icon={Folder}
        active={selectedId === '__unfiled__'}
        count={unfiledCount}
        muted
        onClick={() => onSelect?.('__unfiled__')}
        onDragOver={onDragOver}
        onDrop={(e) => handleDrop(null, e)}
        testId="folder-row-unfiled"
      />

      <div className="pt-2 pb-1 px-2 flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider text-[#8C8078]">Folders</span>
        <button
          type="button"
          onClick={() => { setCreatingUnder('__root__'); setNewName(''); }}
          className="text-[#A86A3A] hover:text-[#8B5730] text-xs flex items-center gap-1"
          data-testid="folder-tree-add-root"
        >
          <Plus className="w-3 h-3" /> New
        </button>
      </div>

      {creatingUnder === '__root__' && (
        <InlineCreate
          value={newName}
          onChange={setNewName}
          onCommit={() => createFolder(null)}
          onCancel={() => setCreatingUnder(null)}
        />
      )}

      <ul className="space-y-0.5" data-testid="folder-tree-root-list">
        {rootFolders.map((f) => (
          <FolderNode
            key={f.id}
            folder={f}
            childrenOf={childrenOf}
            depth={0}
            expanded={expanded}
            setExpanded={setExpanded}
            selectedId={selectedId}
            onSelect={onSelect}
            creatingUnder={creatingUnder}
            setCreatingUnder={setCreatingUnder}
            newName={newName}
            setNewName={setNewName}
            createFolder={createFolder}
            renaming={renaming}
            setRenaming={setRenaming}
            renameValue={renameValue}
            setRenameValue={setRenameValue}
            renameFolder={renameFolder}
            deleteFolder={deleteFolder}
            onDragOver={onDragOver}
            handleDrop={handleDrop}
          />
        ))}
      </ul>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Folder node (recursive)
// ──────────────────────────────────────────────────────────────────────────

function FolderNode({
  folder, childrenOf, depth, expanded, setExpanded,
  selectedId, onSelect, creatingUnder, setCreatingUnder,
  newName, setNewName, createFolder,
  renaming, setRenaming, renameValue, setRenameValue, renameFolder,
  deleteFolder, onDragOver, handleDrop,
}) {
  const children = childrenOf(folder.id);
  const hasChildren = children.length > 0;
  const isOpen = expanded[folder.id];
  const active = selectedId === folder.id;
  const isRenaming = renaming === folder.id;

  return (
    <li>
      <div
        onDragOver={onDragOver}
        onDrop={(e) => handleDrop(folder.id, e)}
        className={cn(
          'group flex items-center gap-1 px-2 py-1.5 rounded-md cursor-pointer transition-colors',
          active ? 'bg-[#FFF8F0] text-[#2C2420]' : 'text-[#5D524D] hover:bg-[#F9F7F5]',
        )}
        style={{ paddingLeft: 8 + depth * 14 }}
        data-testid={`folder-row-${folder.id}`}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setExpanded((x) => ({ ...x, [folder.id]: !isOpen })); }}
            className="text-[#8C8078] shrink-0"
          >
            {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </button>
        ) : (
          <span className="w-3 inline-block" />
        )}
        {isOpen ? <FolderOpen className="w-3.5 h-3.5 text-[#A86A3A] shrink-0" /> : <Folder className="w-3.5 h-3.5 text-[#A86A3A] shrink-0" />}

        {isRenaming ? (
          <Input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') renameFolder(folder.id);
              if (e.key === 'Escape') setRenaming(null);
            }}
            onBlur={() => renameFolder(folder.id)}
            autoFocus
            className="h-6 text-sm flex-1"
            data-testid={`folder-rename-input-${folder.id}`}
          />
        ) : (
          <span
            onClick={() => onSelect?.(folder.id)}
            className="text-sm flex-1 truncate"
          >
            {folder.name}
          </span>
        )}

        {folder.documentCount > 0 && !isRenaming && (
          <span className="text-[10px] text-[#8C8078] tabular-nums">{folder.documentCount}</span>
        )}

        {!isRenaming && (
          <div className="hidden group-hover:flex items-center gap-0.5 shrink-0">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setCreatingUnder(folder.id); setNewName(''); setExpanded((x) => ({ ...x, [folder.id]: true })); }}
              className="p-0.5 text-[#8C8078] hover:text-[#A86A3A]"
              title="New sub-folder"
              data-testid={`folder-add-sub-${folder.id}`}
            >
              <FolderPlus className="w-3 h-3" />
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setRenaming(folder.id); setRenameValue(folder.name); }}
              className="p-0.5 text-[#8C8078] hover:text-[#A86A3A]"
              title="Rename"
              data-testid={`folder-rename-btn-${folder.id}`}
            >
              <PencilLine className="w-3 h-3" />
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); deleteFolder(folder); }}
              className="p-0.5 text-[#8C8078] hover:text-red-600"
              title="Delete"
              data-testid={`folder-delete-btn-${folder.id}`}
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>

      {creatingUnder === folder.id && (
        <div style={{ paddingLeft: 8 + (depth + 1) * 14 }}>
          <InlineCreate
            value={newName}
            onChange={setNewName}
            onCommit={() => createFolder(folder.id)}
            onCancel={() => setCreatingUnder(null)}
          />
        </div>
      )}

      {isOpen && hasChildren && (
        <ul>
          {children.map((c) => (
            <FolderNode
              key={c.id}
              folder={c}
              childrenOf={childrenOf}
              depth={depth + 1}
              expanded={expanded}
              setExpanded={setExpanded}
              selectedId={selectedId}
              onSelect={onSelect}
              creatingUnder={creatingUnder}
              setCreatingUnder={setCreatingUnder}
              newName={newName}
              setNewName={setNewName}
              createFolder={createFolder}
              renaming={renaming}
              setRenaming={setRenaming}
              renameValue={renameValue}
              setRenameValue={setRenameValue}
              renameFolder={renameFolder}
              deleteFolder={deleteFolder}
              onDragOver={onDragOver}
              handleDrop={handleDrop}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Generic row + inline create
// ──────────────────────────────────────────────────────────────────────────

function FolderRow({ label, icon: Icon, active, count, muted, onClick, onDragOver, onDrop, testId }) {
  return (
    <div
      onClick={onClick}
      onDragOver={onDragOver}
      onDrop={onDrop}
      className={cn(
        'flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors',
        active ? 'bg-[#FFF8F0] text-[#2C2420]' : 'text-[#5D524D] hover:bg-[#F9F7F5]',
        muted && !active && 'text-[#8C8078]',
      )}
      data-testid={testId}
    >
      <Icon className="w-3.5 h-3.5 shrink-0" />
      <span className="text-sm flex-1 truncate">{label}</span>
      {count !== undefined && count > 0 && (
        <span className="text-[10px] text-[#8C8078] tabular-nums">{count}</span>
      )}
    </div>
  );
}

function InlineCreate({ value, onChange, onCommit, onCancel }) {
  return (
    <div className="flex items-center gap-1 px-2 py-1">
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onCommit();
          if (e.key === 'Escape') onCancel();
        }}
        placeholder="Folder name…"
        className="h-7 text-sm"
        autoFocus
        data-testid="folder-create-input"
      />
      <Button size="sm" variant="ghost" onClick={onCommit} className="h-7 px-2" data-testid="folder-create-confirm">
        OK
      </Button>
    </div>
  );
}
